import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getReviewProfile } from "../lib/reviewClient.js";
import { findingValueLabel } from "../lib/discoveryQueries.js";

const empty = { status: "unavailable", profile: null, request: () => {} };
const ReferenceLabels = createContext(empty);
export const isReviewReference = (axis) => axis === "normRef" || axis === "intentRef";

export function DiscoveryReferenceLabels({ allowed, contextKey, children }) {
    const [needed, setNeeded] = useState(false);
    const [result, setResult] = useState(null);
    const request = useCallback(() => setNeeded(true), []);
    useEffect(() => {
        if (!allowed || !needed) return;
        let active = true;
        const controller = new AbortController();
        getReviewProfile({ signal: controller.signal }).then((profile) => {
            if (active) setResult({ contextKey, status: "ready", profile });
        }, () => {
            if (active) setResult({ contextKey, status: "error", profile: null });
        });
        return () => { active = false; controller.abort(); };
    }, [allowed, needed, contextKey]);
    const state = !allowed ? empty : result?.contextKey === contextKey ? result : { status: needed ? "loading" : "idle", profile: null };
    return <ReferenceLabels.Provider value={{ ...state, request }}>{children}</ReferenceLabels.Provider>;
}

export function useReviewReferenceLabels(axis) {
    const state = useContext(ReferenceLabels);
    const { request } = state;
    useEffect(() => { if (isReviewReference(axis)) request(); }, [axis, request]);
    return state;
}

export function ReviewReferenceLabel({ axis, value }) {
    const { profile } = useReviewReferenceLabels(axis);
    const items = profile?.[axis === "normRef" ? "norms" : "intents"] ?? [];
    const matches = items.filter((item) => item.reference === value);
    const item = matches.length === 1 ? matches[0] : null;
    const title = item?.title?.trim();
    return title ? <span title={item.description || title}>{title} <span className="discovery-reference-id">({value})</span></span> : findingValueLabel(value);
}

export function ReviewReferenceNotice({ axis }) {
    const { status } = useReviewReferenceLabels(axis);
    if (!isReviewReference(axis)) return null;
    return <p className="discovery-note" aria-busy={status === "loading"}>
        {status === "loading" ? "Loading reference labels..." : status === "ready"
            ? "Labels describe the current review profile. Older findings may use earlier definitions; unmatched references keep their IDs."
            : "Reference labels are unavailable. You can still filter by reference ID."}
    </p>;
}
